import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading, Subheading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../services/firebase';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [isValidCode, setIsValidCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the reset code from the URL
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get('oobCode'); // oobCode is the password reset code
  
  // Verify the reset code when the component mounts
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError('Invalid password reset link. Please request a new one.');
        setIsVerifying(false);
        return;
      }
      
      try {
        // This will verify the code and return the email address
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setIsValidCode(true);
      } catch (err) {
        console.error('Error verifying reset code:', err);
        setError('This password reset link is invalid or has expired. Please request a new one.');
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyCode();
  }, [oobCode]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Confirm the password reset
      await confirmPasswordReset(auth, oobCode, password);
      setIsSuccess(true);
      setMessage('Your password has been reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Error resetting password:', err);
      
      if (err.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Please choose a stronger password.');
      } else {
        setError('An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isVerifying) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
        <Container>
          <div className="mx-auto max-w-md text-center">
            <Logo className="h-12 mx-auto mb-8" />
            <Heading>Verifying your link</Heading>
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent"></div>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <div className="py-24 relative min-h-screen sm:py-32">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
      
      <Container>
        <div className="mx-auto max-w-md">
          <div className="mb-12 text-center">
            <Link to="/">
              <Logo className="h-12 mx-auto mb-8" />
            </Link>
            <Heading>Set new password</Heading>
            {isValidCode && (
              <p className="mt-4 text-lg text-gray-600">
                Please create a new password for <span className="font-medium">{email}</span>
              </p>
            )}
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-8">
            {!isValidCode ? (
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Invalid reset link</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <Link to="/forgot-password">
                  <Button className="w-full">
                    Request new reset link
                  </Button>
                </Link>
              </div>
            ) : isSuccess ? (
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset Successful</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <p className="text-sm text-gray-500 mb-6">You will be redirected to the login page in a few seconds...</p>
                <Link to="/auth">
                  <Button className="w-full">
                    Go to login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="p-3 bg-red-50 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                
                <div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ResetPasswordPage;